import { SoundButton } from "../../common/SoundButton/SoundButton";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import { SimpleDialog } from "../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../common/SimpleDialog/comps/SimpleDialogHeader";
import { useLocalePreference } from "../../../logic/hooks/useLocalePreference";
import { useAudioSettings } from "../../../logic/hooks/useAudioSettings";
import { IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE } from "../../../state/audio";
import { BUILD_ID, BUILD_TIME, CLIENT_VERSION } from "../../../state/config";
import { haptics } from "../../../logic/helpers/haptics";
import { playButtonClickSound } from "../../../logic/helpers/buttonClickSound";
import {
    LANGUAGE_OPTIONS,
    type SupportedLocale,
} from "../../../logic/i18n/locales";
import { FriendsDialog } from "./components/FriendsDialog/FriendsDialog";
import type {
    SpinTogetherController,
    SpinTogetherPresenceMode,
} from "../../../logic/hooks/useSpinTogether";
import "./SettingsDialog.scss";

interface SettingsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    spinTogether: SpinTogetherController;
}

const BUILD_DETAILS_TAP_COUNT = 7;

export function SettingsDialog({
    isOpen,
    onClose,
    spinTogether,
}: SettingsDialogProps) {
    const { t } = useTranslation();
    const { locale, selectLocale, saveState } = useLocalePreference();
    const {
        audioSettings,
        setSlotMachineMusicEnabled,
        setSoundEffectsEnabled,
    } = useAudioSettings();
    const buildTapCountRef = useRef(0);
    const [showBuildDetails, setShowBuildDetails] = useState(false);
    const [isFriendsOpen, setIsFriendsOpen] = useState(false);
    const statusText =
        saveState === "saving"
            ? t("settings.saving")
            : saveState === "pending"
              ? t("settings.pending")
              : t("settings.syncHint");

    useEffect(() => {
        if (!isOpen) {
            buildTapCountRef.current = 0;
            setShowBuildDetails(false);
            setIsFriendsOpen(false);
        }
    }, [isOpen]);

    const handleBuildTap = () => {
        if (buildTapCountRef.current >= BUILD_DETAILS_TAP_COUNT) return;

        buildTapCountRef.current += 1;
        if (buildTapCountRef.current === BUILD_DETAILS_TAP_COUNT) {
            setShowBuildDetails(true);
        }
    };

    const copyBuildValue = (value: string) => {
        if (!navigator.clipboard) return;
        void navigator.clipboard.writeText(value).catch(() => undefined);
    };

    const clearAppCache = async () => {
        haptics.impact("medium");

        try {
            window.localStorage.clear();
        } catch {
            // Storage can be blocked or unavailable.
        }

        try {
            window.sessionStorage.clear();
        } catch {
            // Storage can be blocked or unavailable.
        }

        try {
            const cacheNames = await window.caches.keys();
            await Promise.all(
                cacheNames.map((cacheName) => window.caches.delete(cacheName)),
            );
        } catch {
            // Cache Storage can be blocked or unavailable.
        }

        window.location.reload();
    };

    const saveSpinTogether = (
        patch: Parameters<SpinTogetherController["updatePreference"]>[0],
    ) => {
        playButtonClickSound();
        void spinTogether.updatePreference(patch).catch(() => undefined);
    };

    return (
        <>
            <SimpleDialog
                className="settings-dialog"
                isOpen={isOpen && !isFriendsOpen}
                onClose={onClose}
                aria-labelledby="settings-dialog-title"
                header={
                    <SimpleDialogHeader
                        title={t("settings.title")}
                        titleId="settings-dialog-title"
                        closeLabel={t("settings.close")}
                        onClose={onClose}
                    />
                }
            >
                <div className="settings-dialog__body">
                    <label
                        className="settings-dialog__field"
                        htmlFor="settings-language"
                    >
                        <span>{t("settings.language")}</span>
                        <select
                            id="settings-language"
                            value={locale}
                            onChange={(event) => {
                                playButtonClickSound();
                                void selectLocale(
                                    event.currentTarget
                                        .value as SupportedLocale,
                                );
                            }}
                        >
                            {LANGUAGE_OPTIONS.map((language) => (
                                <option
                                    key={language.code}
                                    value={language.code}
                                    dir="auto"
                                >
                                    {language.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    <p role="status" aria-live="polite">
                        {statusText}
                    </p>

                    <fieldset className="settings-dialog__audio">
                        <legend>{t("settings.audio.title")}</legend>
                        <label
                            className={[
                                "settings-dialog__toggle",
                                !IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE &&
                                    "settings-dialog__toggle--disabled",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        >
                            <input
                                type="checkbox"
                                checked={
                                    IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE &&
                                    audioSettings.slotMachineMusicEnabled
                                }
                                disabled={
                                    !IS_SLOT_MACHINE_BACKGROUND_MUSIC_AVAILABLE
                                }
                                onChange={(event) => {
                                    haptics.impact("light");
                                    setSlotMachineMusicEnabled(
                                        event.currentTarget.checked,
                                    );
                                    playButtonClickSound();
                                }}
                            />
                            <span>
                                {t("settings.audio.slotMachineMusic")}
                            </span>
                        </label>
                        <label className="settings-dialog__toggle">
                            <input
                                type="checkbox"
                                checked={audioSettings.soundEffectsEnabled}
                                onChange={(event) => {
                                    haptics.impact("light");
                                    setSoundEffectsEnabled(
                                        event.currentTarget.checked,
                                    );
                                }}
                            />
                            <span>{t("settings.audio.soundEffects")}</span>
                        </label>
                    </fieldset>

                    {spinTogether.preference && (
                        <fieldset className="settings-dialog__spin-together">
                            <legend>
                                {t("spinTogether.title", {
                                    defaultValue: "Spin Together",
                                })}
                            </legend>
                            <label
                                className="settings-dialog__field"
                                htmlFor="settings-spin-together-presence"
                            >
                                <span>
                                    {t("spinTogether.onlineStatus", {
                                        defaultValue: "Online status",
                                    })}
                                </span>
                                <select
                                    id="settings-spin-together-presence"
                                    value={spinTogether.preference.presenceMode}
                                    disabled={
                                        spinTogether.mutationStatus === "saving"
                                    }
                                    onChange={(event) =>
                                        saveSpinTogether({
                                            presenceMode: event.currentTarget
                                                .value as SpinTogetherPresenceMode,
                                        })
                                    }
                                >
                                    <option value="ONLINE">
                                        {t("spinTogether.online", {
                                            defaultValue: "Online",
                                        })}
                                    </option>
                                    <option value="BUSY">
                                        {t("spinTogether.busy", {
                                            defaultValue: "Busy",
                                        })}
                                    </option>
                                    <option value="APPEAR_OFFLINE">
                                        {t("spinTogether.appearOffline", {
                                            defaultValue: "Appear Offline",
                                        })}
                                    </option>
                                </select>
                            </label>
                            <label className="settings-dialog__toggle">
                                <input
                                    type="checkbox"
                                    checked={
                                        spinTogether.preference
                                            .participationEnabled
                                    }
                                    disabled={
                                        spinTogether.mutationStatus === "saving"
                                    }
                                    onChange={(event) =>
                                        saveSpinTogether({
                                            participationEnabled:
                                                event.currentTarget.checked,
                                        })
                                    }
                                />
                                <span>
                                    {t("spinTogether.participation", {
                                        defaultValue:
                                            "Enable play-together bonuses",
                                    })}
                                </span>
                            </label>
                            <label className="settings-dialog__toggle">
                                <input
                                    type="checkbox"
                                    checked={
                                        spinTogether.preference
                                            .shareExactWinnings
                                    }
                                    disabled={
                                        spinTogether.mutationStatus === "saving"
                                    }
                                    onChange={(event) =>
                                        saveSpinTogether({
                                            shareExactWinnings:
                                                event.currentTarget.checked,
                                        })
                                    }
                                />
                                <span>
                                    {t("spinTogether.shareExactWinnings", {
                                        defaultValue:
                                            "Share exact winnings with friends",
                                    })}
                                </span>
                            </label>
                            <label className="settings-dialog__toggle">
                                <input
                                    type="checkbox"
                                    checked={
                                        spinTogether.preference.allowInvites
                                    }
                                    disabled={
                                        spinTogether.mutationStatus === "saving"
                                    }
                                    onChange={(event) =>
                                        saveSpinTogether({
                                            allowInvites:
                                                event.currentTarget.checked,
                                        })
                                    }
                                />
                                <span>
                                    {t("spinTogether.allowInvites", {
                                        defaultValue:
                                            "Allow live play invitations",
                                    })}
                                </span>
                            </label>
                            <p>
                                {spinTogether.preference.presenceMode ===
                                "APPEAR_OFFLINE"
                                    ? t("spinTogether.offlineEconomics", {
                                          defaultValue:
                                              "Appear Offline also pauses Spin Together bonuses and activity sharing.",
                                      })
                                    : t("spinTogether.privacyHint", {
                                          defaultValue:
                                              "Your balance is never shared.",
                                      })}
                            </p>
                            {spinTogether.error && (
                                <p role="alert">{spinTogether.error}</p>
                            )}
                        </fieldset>
                    )}

                    <SoundButton
                        type="button"
                        className="settings-dialog__friends"
                        onClick={() => {
                            haptics.impact("light");
                            setIsFriendsOpen(true);
                        }}
                    >
                        {t("settings.friends")}
                    </SoundButton>

                    <div className="settings-dialog__build">
                        <SoundButton
                            type="button"
                            aria-controls="settings-build-details"
                            aria-expanded={showBuildDetails}
                            onClick={handleBuildTap}
                        >
                            {t("settings.build", { buildId: BUILD_ID })}
                        </SoundButton>

                        {showBuildDetails && (
                            <dl id="settings-build-details">
                                <dt>{t("settings.commit")}</dt>
                                <dd
                                    className="settings-dialog__build-value"
                                    dir="ltr"
                                >
                                    <span>{CLIENT_VERSION}</span>
                                    <SoundButton
                                        type="button"
                                        aria-label={t("settings.copyValue", {
                                            label: t("settings.commit"),
                                        })}
                                        title={t("settings.copyValue", {
                                            label: t("settings.commit"),
                                        })}
                                        onClick={() =>
                                            copyBuildValue(CLIENT_VERSION)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faCopy} />
                                    </SoundButton>
                                </dd>
                                <dt>{t("settings.built")}</dt>
                                <dd
                                    className="settings-dialog__build-value"
                                    dir="ltr"
                                >
                                    <time dateTime={BUILD_TIME}>
                                        {BUILD_TIME}
                                    </time>
                                    <SoundButton
                                        type="button"
                                        aria-label={t("settings.copyValue", {
                                            label: t("settings.built"),
                                        })}
                                        title={t("settings.copyValue", {
                                            label: t("settings.built"),
                                        })}
                                        onClick={() =>
                                            copyBuildValue(BUILD_TIME)
                                        }
                                    >
                                        <FontAwesomeIcon icon={faCopy} />
                                    </SoundButton>
                                </dd>
                                <dt>{t("settings.appCache")}</dt>
                                <dd className="settings-dialog__build-cache">
                                    <SoundButton
                                        type="button"
                                        onClick={() => void clearAppCache()}
                                    >
                                        {t("settings.clearCache")}
                                    </SoundButton>
                                </dd>
                            </dl>
                        )}
                    </div>
                </div>
            </SimpleDialog>

            <FriendsDialog
                isOpen={isOpen && isFriendsOpen}
                onClose={() => setIsFriendsOpen(false)}
            />
        </>
    );
}
